// Ultra-optimized structure without redundancies
// Only trips with their relationships, without unnecessary fields
export interface TripRelationships {
  destinations: string[];
  activities: string[];
}

// Ultra-optimized database
// Only trips with their relationships, without redundancies
export const trips: Record<string, TripRelationships> = {
  'winter-trip': {
    destinations: [],
    activities: []
  },
  'summer-trip': {
    destinations: ['collalto_sabino', 'santo_stefano_di_sessanio', 'rocca_calascio', 'campo_imperatore', 'scanno', 'trabocchi_coast'],
    activities: ['cantina_tilli', 'sextantio', 'arrosticini_mucciante', 'wild_life_spotting']
  }
};

// Helper functions to get relationships
// All relationships are calculated dynamically from trips

export const getRelatedTrips = (entityId: string): string[] => {
  // Find all trips that have this entity in their relationships
  return Object.entries(trips)
    .filter(([_, tripRelationships]) => 
      tripRelationships.destinations.includes(entityId) ||
      tripRelationships.activities.includes(entityId)
    )
    .map(([tripId, _]) => tripId);
};

export const getRelatedDestinations = (entityId: string): string[] => {
  // For trips: direct access
  if (trips[entityId]) {
    return trips[entityId].destinations;
  }
  
  // For destinations and activities: dynamic calculation
  return Object.entries(trips)
    .filter(([_, tripRelationships]) => 
      tripRelationships.destinations.includes(entityId) ||
      tripRelationships.activities.includes(entityId)
    )
    .flatMap(([_, tripRelationships]) => tripRelationships.destinations)
    .filter((destId, index, array) => array.indexOf(destId) === index); // Remove duplicates
};

export const getRelatedActivities = (entityId: string): string[] => {
  // For trips: direct access
  if (trips[entityId]) {
    return trips[entityId].activities;
  }
  
  // For destinations and activities: dynamic calculation
  return Object.entries(trips)
    .filter(([_, tripRelationships]) => 
      tripRelationships.destinations.includes(entityId) ||
      tripRelationships.activities.includes(entityId)
    )
    .flatMap(([_, tripRelationships]) => tripRelationships.activities)
    .filter((activityId, index, array) => array.indexOf(activityId) === index); // Remove duplicates
};

// Specific functions for backward compatibility (maintain the same API)
export const getTripsForDestination = (destinationId: string): string[] => {
  return getRelatedTrips(destinationId);
};

export const getTripsForActivity = (activityId: string): string[] => {
  return getRelatedTrips(activityId);
};

export const getDestinationsForTrip = (tripId: string): string[] => {
  return getRelatedDestinations(tripId);
};

export const getActivitiesForTrip = (tripId: string): string[] => {
  return getRelatedActivities(tripId);
};

export const getActivitiesForDestination = (destinationId: string): string[] => {
  return getRelatedActivities(destinationId);
};

export const getDestinationsForActivity = (activityId: string): string[] => {
  return getRelatedDestinations(activityId);
};

// Functions to add/remove relationships
// These functions only modify trips, maintaining unidirectional consistency

export const addRelationship = (
  tripId: string,
  relatedEntityId: string,
  relatedEntityType: 'destination' | 'activity'
): void => {
  // Ensure the trip exists
  if (!trips[tripId]) {
    throw new Error(`Trip ${tripId} not found`);
  }
  
  // Add the relationship only to the trip (avoiding duplicates)
  if (relatedEntityType === 'destination' && !trips[tripId].destinations.includes(relatedEntityId)) {
    trips[tripId].destinations.push(relatedEntityId);
  } else if (relatedEntityType === 'activity' && !trips[tripId].activities.includes(relatedEntityId)) {
    trips[tripId].activities.push(relatedEntityId);
  }
};

export const removeRelationship = (
  tripId: string,
  relatedEntityId: string,
  relatedEntityType: 'destination' | 'activity'
): void => {
  const trip = trips[tripId];
  
  if (trip) {
    // Remove from trip relationship
    if (relatedEntityType === 'destination') {
      const index = trip.destinations.indexOf(relatedEntityId);
      if (index > -1) trip.destinations.splice(index, 1);
    } else if (relatedEntityType === 'activity') {
      const index = trip.activities.indexOf(relatedEntityId);
      if (index > -1) trip.activities.splice(index, 1);
    }
  }
};

// Function to check if a relationship exists
export const hasRelationship = (
  tripId: string,
  relatedEntityId: string,
  relatedEntityType: 'destination' | 'activity'
): boolean => {
  const trip = trips[tripId];
  if (!trip) return false;
  
  if (relatedEntityType === 'destination') {
    return trip.destinations.includes(relatedEntityId);
  } else if (relatedEntityType === 'activity') {
    return trip.activities.includes(relatedEntityId);
  }
  
  return false;
};

// Function to get all relationships of an entity
// For destinations and activities, returns null (they don't store relationships)
export const getAllRelationships = (entityId: string): TripRelationships | null => {
  return trips[entityId] || null;
};

// Alias for backward compatibility
export const relationships = trips; 